import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { Lead } from "@/types";
import { MOCK_DEALER_ID, saveLeadToSupabase } from "@/services/leads";

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().min(10, { message: "Please enter a valid phone number" }),
  meta_notes: z.string().optional(),
});

type FormSchema = z.infer<typeof formSchema>;

interface LeadFormProps {
  onComplete?: () => void;
}

export const LeadForm = ({ onComplete }: LeadFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      meta_notes: "",
    },
  });

  const onSubmit = async (data: FormSchema) => {
    setIsSubmitting(true);
    
    try {
      const newLead: Lead = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        meta_notes: data.meta_notes || "",
        dealer_id: MOCK_DEALER_ID,
        status: "queued",
        created_at: new Date().toISOString(),
      };
      
      const { success, error } = await saveLeadToSupabase(newLead);
      
      if (success) {
        toast({
          title: "Lead added successfully",
          description: "The lead has been added to your queue.",
        });
        
        form.reset();
        
        if (onComplete) {
          onComplete();
        }
      } else {
        throw new Error(error);
      }
    } catch (error) {
      console.error("Failed to add lead:", error);
      toast({
        title: "Error adding lead",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name*</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email*</FormLabel>
              <FormControl>
                <Input placeholder="john@example.com" type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone*</FormLabel>
              <FormControl>
                <Input placeholder="(555) 123-4567" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="meta_notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Any additional information about this lead" 
                  className="resize-none" 
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? "Adding..." : "Add Lead"}
        </Button>
      </form>
    </Form>
  );
};
